/* Una catena del wallet: saldo nativo, token, avvisi.
 *
 * Una catena che non risponde SI MOSTRA. Nasconderla farebbe leggere il totale
 * come se fosse completo, e quel totale è la base su cui si decide se un bot
 * può partire. Qui l'errore della catena diventa una riga in ambra — non in
 * rosso: il rosso significa perdita, non guasto.
 */

import { Avvertenza, Distintivo } from '../../componenti';
import { valuta } from '../../lib/formato';
import { importoToken, urlEsploratore } from './dati';
import type { SaldiCatena, SaldoNativo, SaldoToken } from '../../api/tipi';

export function SchedaCatena({
  catena,
  indirizzo,
}: {
  catena: SaldiCatena;
  indirizzo: string | null;
}) {
  const link = urlEsploratore(catena, indirizzo);
  const token = catena.tokens ?? [];
  /* Il totale in dollari copre solo i token di cui si conosce il prezzo: se
     alcuni non ne hanno, il totale è parziale e va detto accanto al numero.
     Niente `?? 0` sul conteggio: se `pricedTokens` non arrivasse, `senzaPrezzo`
     diventerebbe pari a TUTTI i token e l'avviso accuserebbe l'intera catena di
     essere senza prezzi — un conteggio inventato, per giunta al valore massimo
     possibile. Senza ripiego la sottrazione non è un numero e l'avviso non
     compare: davanti a un conteggio che non c'è si tace, non si accusa. */
  const senzaPrezzo = token.length - catena.pricedTokens;

  return (
    <section className={`sb-w-catena${catena.ok ? '' : ' sb-w-catena--muta'}`}>
      <header className="sb-w-catena__testata">
        <span
          className={`sb-w-punto ${catena.ok ? 'sb-w-punto--viva' : 'sb-w-punto--muta'}`}
          aria-hidden="true"
        />
        <span className="sb-w-catena__nome">{catena.name}</span>
        {/* La parola accanto al pallino: il colore da solo non è un'informazione. */}
        <Distintivo tono={catena.ok ? 'neutro' : 'cautela'}>
          {catena.ok ? 'letta' : 'muta'}
        </Distintivo>
        <span className="sb-w-catena__usd">
          {catena.totalUsd == null ? 'n/d' : valuta(catena.totalUsd, '$', 2)}
        </span>
      </header>

      <div className="sb-w-catena__corpo">
        <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
          <span className="sb-nota">
            {catena.ok
              ? /* «0 token letti» su una catena marcata `letta` afferma una
                   scansione vuota che nessuno ha misurato — ed è indistinguibile
                   da una catena davvero senza token. Il conteggio assente si
                   dichiara, come già faceva il numero di blocco accanto. */
                `blocco ${catena.blockNumber ?? '—'} · ${
                  catena.scannedTokens == null
                    ? 'quanti token siano stati letti non è dichiarato'
                    : `${catena.scannedTokens} token letti`
                } · lettura in ${catena.durationMs} ms`
              : 'nessuna lettura da questa catena'}
          </span>
          {link && (
            <a href={link} target="_blank" rel="noreferrer noopener" className="sb-nota" style={{ color: 'var(--sb-accento-chiaro)' }}>
              apri sull'esploratore
            </a>
          )}
        </div>

        {senzaPrezzo > 0 && (
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)', color: 'var(--sb-cautela-testo)' }}>
            totale parziale: {senzaPrezzo} {senzaPrezzo === 1 ? 'token senza prezzo' : 'token senza prezzo'}, non
            conteggiati
          </div>
        )}
      </div>

      {/* L'errore della catena viene dal server già in italiano. */}
      {!catena.ok && catena.error && (
        <div style={{ padding: '0 var(--sb-s-7) var(--sb-s-6)' }}>
          <Avvertenza livello="cautela">
            {catena.name} non risponde: {catena.error}. Il saldo mostrato altrove non comprende questa catena.
          </Avvertenza>
        </div>
      )}

      {catena.gasAlert && (
        <div style={{ padding: '0 var(--sb-s-7) var(--sb-s-6)' }}>
          <Avvertenza livello={catena.gasAlert.level === 'low' ? 'cautela' : 'grave'}>
            {catena.gasAlert.message} — bastano per {catena.gasAlert.swapsCovered}{' '}
            {catena.gasAlert.swapsCovered === 1 ? 'operazione' : 'operazioni'}; il minimo consigliato è{' '}
            {importoToken(String(catena.gasAlert.recommendedMinimum))} {catena.nativeSymbol}.
          </Avvertenza>
        </div>
      )}

      {catena.warnings?.map((a) => (
        <div key={a} style={{ padding: '0 var(--sb-s-7) var(--sb-s-6)' }}>
          <Avvertenza livello="cautela">{a}</Avvertenza>
        </div>
      ))}

      {catena.native && (
        <RigaToken
          saldo={catena.native}
          nativo
          etichettaNativa={`${catena.nativeSymbol} · moneta di gas`}
        />
      )}

      {token.map((t) => (
        <RigaToken key={`${t.address}-${t.symbol}`} saldo={t} />
      ))}

      {catena.ok && !catena.native && token.length === 0 && (
        <div className="sb-w-token">
          <span className="sb-nota">Nessun saldo diverso da zero su {catena.name}.</span>
        </div>
      )}
    </section>
  );
}

function RigaToken({
  saldo,
  nativo = false,
  etichettaNativa,
}: {
  saldo: SaldoNativo | SaldoToken;
  nativo?: boolean;
  etichettaNativa?: string;
}) {
  const nonVerificato = 'verified' in saldo && saldo.verified === false;

  return (
    <div className="sb-w-token">
      <span className="sb-w-token__sigla">
        {saldo.symbol}
        {nativo && (
          <span className="sb-nota" style={{ display: 'block', marginTop: 4 }}>
            {etichettaNativa}
          </span>
        )}
        {/* Un token letto dal contratto e non da una lista curata può avere un
            nome scelto da chi l'ha creato: va detto, non filtrato via. */}
        {nonVerificato && (
          <span className="sb-nota" style={{ display: 'block', marginTop: 4, color: 'var(--sb-cautela-testo)' }}>
            non verificato · nome preso dal contratto
          </span>
        )}
      </span>
      <span className="sb-w-token__valori">
        <span className="sb-w-token__quantita">{importoToken(saldo.balance)}</span>
        <span className="sb-w-token__usd">
          {saldo.balanceUsd == null ? 'prezzo non disponibile' : valuta(saldo.balanceUsd, '$', 2)}
        </span>
      </span>
    </div>
  );
}
